<script lang="ts">
import type { Nullable } from '@/types'
import { defineComponent } from 'vue'

/**
 * CarouselPagination
 */
export default defineComponent({
  props: {
    /**
     * The elements to paginate through.
     */
    elements: {
      type: Array<HTMLElement>,
      required: true,
    },
    /**
     * The current index of the pagination.
     * This is used to highlight the current bullet.
     */
    currentIndex: {
      type: Number,
      default: 0,
    },
    /**
     * The number of bullets to show on either side of the current index.
     * This determines how many pagination bullets are visible at once.
     */
    bulletCount: {
      type: Number,
      default: 2,
    },
  },
  emits: ['scrollTo'],
  data() {
    return {
      visibleBullets: [] as Array<HTMLElement>,
    }
  },
  computed: {
    /**
     * Returns the current bullet element based on the current index.
     *
     * @returns The current bullet element or null if not found.
     */
    currentBullet(): Nullable<HTMLElement> {
      return (
        this.visibleBullets.find((bullet) => {
          return bullet === this.elements[this.currentIndex]
        }) ?? null
      )
    },
  },
  watch: {
    currentIndex() {
      this.updateVisibleBullets()
    },
  },
  mounted() {
    this.updateVisibleBullets()
  },
  methods: {
    /**
     * Updates the visible bullets based on the current index.
     */
    updateVisibleBullets() {
      const start = Math.max(
        Math.min(
          this.currentIndex > this.elements.length - this.bulletCount
            ? this.elements.length - this.bulletCount * 2
            : this.currentIndex - this.bulletCount
        ),
        0
      )
      const end = Math.min(
        this.currentIndex <= this.bulletCount
          ? this.bulletCount * 2 + 1
          : this.currentIndex + this.bulletCount * 2 - 1,
        this.elements.length
      )

      this.visibleBullets = this.elements.slice(start, end)
    },
    /**
     * Returns the index of the given element in the elements array.
     *
     * @param {HTMLElement} element The element to find the index of.
     * @returns The index of the element, or -1 if not found.
     */
    getElementIndex(element: HTMLElement): number {
      return this.elements.indexOf(element)
    },
  },
})
</script>

<template>
  <div class="mx-auto mb-4 mt-8 flex w-full justify-center gap-2">
    <div
      v-for="bullet in visibleBullets"
      :key="bullet.id"
      class="pagination-bullet size-4 rounded-full hover:cursor-pointer"
      :class="{
        'w-10 bg-tertiary': bullet === currentBullet,
        'bg-black/20': bullet !== currentBullet,
      }"
      :aria-label="`${getElementIndex(bullet)} / ${elements.length - 1}`"
      :aria-current="bullet === currentBullet"
      @click="$emit('scrollTo', getElementIndex(bullet))"
    ></div>
  </div>
</template>

<style lang="scss" scoped>
.pagination-bullet {
  transition:
    all 0.3s ease-in-out,
    background-color 0.3s ease-in-out;
}
</style>
